import React, { useState } from 'react';
import { useSite } from '../content/SiteContext';
import { serviceSchema } from '../lib/schema';
import { sendToWhatsApp, formatMessage, isValidEmail, isValidUaeMobile } from '../lib/contact';
import { useFleetRequests } from '../components/FleetRequestsContext';
import { PageMeta, PageHero, SectionHead, Icon, Field, describedBy, FaqList } from '../components/ui';

const FLEET_SIZES = ['5–10 vehicles', '11–25 vehicles', '26–50 vehicles', '51–100 vehicles', '100+ vehicles'];
const FREQUENCIES = ['Weekly', 'Twice a week', 'Every two weeks', 'Monthly', 'Not sure yet'];

const WHO = [
  { icon: 'building', title: 'Corporate offices', text: 'Company cars and executive vehicles washed in your office car park during working hours.' },
  { icon: 'truck', title: 'Fleets and pool vehicles', text: 'Scheduled washes for pool cars, sales fleets and service vehicles at your depot or yard.' },
  { icon: 'users', title: 'Staff car programmes', text: 'Offer car washing as a staff perk, with washes at the office car park.' }
];

const FAQS = [
  { q: 'What is the minimum fleet size?', a: 'Corporate quotes start from 5 vehicles. For fewer cars, our monthly plans may suit you better.' },
  { q: 'Where do you wash fleet vehicles?', a: 'At your office, depot or yard in Abu Dhabi or Dubai, subject to site access and any building rules.' },
  { q: 'How is fleet pricing worked out?', a: 'Pricing depends on the number and type of vehicles, the services needed, how often and where. Send your details and we will prepare a quote.' }
];

const EMPTY = { companyName: '', contactName: '', email: '', phone: '', fleetSize: '', location: '', frequency: '', message: '' };

const Corporate = () => {
  const { CITIES } = useSite();
  const { submitFleetRequest } = useFleetRequests();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sentUrl, setSentUrl] = useState(null);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = 'Enter your company name.';
    if (!form.contactName.trim()) e.contactName = 'Enter a contact name.';
    if (!isValidEmail(form.email)) e.email = 'Enter a valid email address.';
    if (!isValidUaeMobile(form.phone)) e.phone = 'Enter a UAE mobile number, for example 050 123 4567.';
    if (!form.fleetSize) e.fleetSize = 'Choose your fleet size.';
    if (!form.location) e.location = 'Choose an emirate.';
    return e;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (sentUrl) return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    submitFleetRequest(form);
    const text = formatMessage('Fleet car wash quote request', [
      ['Company', form.companyName], ['Contact', form.contactName], ['Email', form.email], ['Phone', form.phone],
      ['Fleet size', form.fleetSize], ['Emirate', form.location], ['Frequency', form.frequency], ['Requirements', form.message]
    ]);
    setSentUrl(sendToWhatsApp(text, 'generate_lead', { lead_type: 'fleet', fleet_size: form.fleetSize, emirate: form.location }));
  };

  const input = (name, label, type = 'text', props = {}) => (
    <Field label={label} id={name} error={errors[name]} required={props.required}>
      <input id={name} name={name} type={type} value={form[name]} onChange={set} aria-invalid={!!errors[name]}
        aria-describedby={describedBy(name, errors[name])} {...props} />
    </Field>
  );

  return (
    <>
      <PageMeta
        title="Corporate & Fleet Car Wash Abu Dhabi & Dubai | Quick Way"
        description="On-site fleet car wash for company vehicles in Abu Dhabi and Dubai. Scheduled washes at your office or depot, quotes for fleets from 5 vehicles."
        path="/corporate-fleet-car-wash"
        crumbs={[{ name: 'Corporate and fleet', path: '/corporate-fleet-car-wash' }]}
        jsonLd={[serviceSchema({ name: 'Fleet car wash', description: 'On-site car washing for company fleets in Abu Dhabi and Dubai.', path: '/corporate-fleet-car-wash' })]}
      />
      <PageHero eyebrow="Corporate and fleet" title="Fleet and corporate car wash services"
        actions={<a href="#quote" className="btn btn-primary btn-lg">Request a quote</a>}>
        <p>Keep company vehicles clean without taking them off the road. We wash at your office, depot or yard in Abu Dhabi and Dubai on a schedule that fits your operation.</p>
      </PageHero>

      <section className="section">
        <div className="container">
          <SectionHead title="Who it is for" />
          <div className="grid grid-3">
            {WHO.map((w) => (
              <div key={w.title} className="feature">
                <span className="icon-tile"><Icon name={w.icon} /></span>
                <h3>{w.title}</h3>
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tint" id="quote">
        <div className="container split split--top">
          <div className="prose">
            <h2>What we can arrange</h2>
            <p>Every fleet is different, so we put together a quote based on your vehicles, location and how often you need them cleaned.</p>
            <ul className="checklist checklist--check">
              {['Washes carried out on your premises', 'A regular schedule agreed with you', 'Volume pricing for larger fleets', 'Packages tailored to vehicle type', 'One point of contact for scheduling'].map((t) => (
                <li key={t}><Icon name="check" size={18} />{t}</li>
              ))}
            </ul>
            <h3>What happens next</h3>
            <p>Sending the form opens WhatsApp with your details filled in. Press send in WhatsApp and our team will reply to discuss your requirements and prepare a quote.</p>
          </div>

          <div className="form-card">
            <h2 className="form-card__title">Request a fleet quote</h2>
            {sentUrl ? (
              <div className="form-success" role="status">
                <Icon name="chat" size={28} />
                <h3>Almost done: press send in WhatsApp</h3>
                <p>Your quote request is ready in WhatsApp. It reaches us once you press send. If WhatsApp did not open, use the button below.</p>
                <a href={sentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">Open WhatsApp</a>
                <button type="button" className="btn-link" onClick={() => { setSentUrl(null); setForm(EMPTY); }}>Start a new request</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <div className="form-grid">
                  {input('companyName', 'Company name', 'text', { required: true, autoComplete: 'organization' })}
                  {input('contactName', 'Contact name', 'text', { required: true, autoComplete: 'name' })}
                  {input('email', 'Work email', 'email', { required: true, autoComplete: 'email' })}
                  {input('phone', 'Mobile number', 'tel', { required: true, autoComplete: 'tel', inputMode: 'tel', placeholder: '050 123 4567' })}
                  <Field label="Fleet size" id="fleetSize" error={errors.fleetSize} required>
                    <select id="fleetSize" name="fleetSize" value={form.fleetSize} onChange={set} aria-invalid={!!errors.fleetSize} aria-describedby={describedBy('fleetSize', errors.fleetSize)}>
                      <option value="">Select</option>
                      {FLEET_SIZES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Emirate" id="location" error={errors.location} required>
                    <select id="location" name="location" value={form.location} onChange={set} aria-invalid={!!errors.location} aria-describedby={describedBy('location', errors.location)}>
                      <option value="">Select</option>
                      {CITIES.map((c) => <option key={c.id}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="How often?" id="frequency">
                    <select id="frequency" name="frequency" value={form.frequency} onChange={set}>
                      <option value="">Select</option>
                      {FREQUENCIES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Requirements" id="message" hint="Vehicle types, site address, preferred days or anything else we should know.">
                  <textarea id="message" name="message" rows="4" value={form.message} onChange={set} aria-describedby="message-hint" />
                </Field>
                <button type="submit" className="btn btn-primary btn-block btn-lg">Send quote request via WhatsApp</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          <SectionHead title="Fleet washing questions" />
          <FaqList items={FAQS} />
        </div>
      </section>
    </>
  );
};

export default Corporate;
